import { Stack } from "@mui/system";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../store";
import { Box, Button, Card, Typography } from "@mui/material";
import { memo, useEffect, useState } from "react";
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium";
import BludataDefaultMainHeader from "../../../UI/BludataDefaultMainHeader";
import { BluAlert, BluLoader } from "@bludata/components";
import columnsHandler from "./columns";
import { customAlphabet } from "nanoid";
import Catalogo from "../../../interfaces/catalogo";
import fetchCatalogo from "../../../api/catalogo/fetchCatalogo";
import deleteCatalogo from "../../../api/catalogo/deleteCatalogo";
import fetchGruppo from "../../../api/gruppi/fetchGruppo";
import Gruppo from "../../../interfaces/gruppo";
import { setTreeItemList } from "../../../store/root";
import DialogModificaCatalogo from "./DialogModificaCatalogo";

export const ConfigurazioneCataloghi = memo(() => {
  const numberAlphabet = "0123456789";
  const { loginData } = useSelector((state: RootState) => state.root);
  const [catalogoList, setCatalogoList] = useState<Catalogo[]>([]);
  const [fileTooBig, setFileTooBig] = useState(false);
  const [group, setGroup] = useState<Gruppo[]>([]);
  const [loading, setLoading] = useState(false);
  const gridApiRef = useGridApiRef();
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const dispatch = useDispatch();
  const [dialogCatalogo, setDialogCatalogo] = useState<{
    catalogo: Catalogo;
    isEdit: boolean;
  } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [selectedCatalogo, setSelectedCatalogo] = useState<Catalogo | null>(
    null
  );

  const reloadCatalogo = () => {
    if (loginData?.admin) {
      setLoading(true);
      fetchCatalogo()
        .then((res) => {
          setLoading(false);

          // Preserve selection if possible
          if (selectedCatalogo) {
            const stillExists = res.find(
              (item) => item.id === selectedCatalogo.id
            );
            if (!stillExists) {
              setSelectedCatalogo(null);
            } else {
              // Update selected catalog with fresh data
              setSelectedCatalogo(stillExists);
            }
          }

          setCatalogoList(res);
          dispatch(
            setTreeItemList(
              res.map((item) => {
                return {
                  gruppo: item.gruppi[0].gruppo,
                  tipo: item.tipo,
                  file: item.file,
                  descrizione: item.descrizione,
                };
              })
            )
          );
        })
        .catch((err) => {
          setError(err || "Internal Server Error");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const reloadGroups = () => {
    if (loginData?.admin) {
      setLoading(true);
      fetchGruppo()
        .then((res) => {
          setLoading(false);
          setGroup(res);
        })
        .catch((err) => {
          setError(err || "Internal Server Error");
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  useEffect(() => {
    reloadCatalogo();
    reloadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onNuovoClick = () => {
    const id = Number(customAlphabet(numberAlphabet, 10)());
    const newCatalogo = {
      id: id,
      tipo: "",
      file: "",
      tabella: null,
      descrizione: "",
      create: true,
      gruppi: [],
    };
    setDialogCatalogo({
      catalogo: newCatalogo,
      isEdit: false,
    });
  };

  const onEditClick = (catalogo: Catalogo) => {
    setDialogCatalogo({
      catalogo,
      isEdit: true,
    });
  };

  const onDeleteClick = (catalogoId: number) => {
    setConfirmDelete(catalogoId);
  };

  const handleDeleteConfirm = () => {
    if (confirmDelete === null) return;

    setLoading(true);
    deleteCatalogo(confirmDelete)
      .then(() => {
        setSuccess("Catalogo eliminato con successo.");
        reloadCatalogo();
      })
      .catch((err) => {
        setError(err || "Errore durante l'eliminazione");
      })
      .finally(() => {
        setLoading(false);
        setConfirmDelete(null);
      });
  };

  const onDialogClose = (savedItemId?: number) => {
    // Keep track of the ID to select after reload
    const idToSelect =
      savedItemId ||
      (dialogCatalogo?.isEdit ? dialogCatalogo.catalogo.id : undefined);

    reloadCatalogo();
    setDialogCatalogo(null);

    // Select the newly created or edited catalog after reload
    if (idToSelect) {
      setTimeout(() => {
        fetchCatalogo().then((cataloghi) => {
          const catalogoToSelect = cataloghi.find((c) => c.id === idToSelect);
          if (catalogoToSelect) {
            setSelectedCatalogo(catalogoToSelect);
          }
        });
      }, 300); // Small delay to ensure data is loaded
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        height: "100%",
        backgroundColor: "var(--hovered-background-color)",
      }}
    >
      {dialogCatalogo && (
        <DialogModificaCatalogo
          onClose={onDialogClose}
          catalogo={dialogCatalogo.catalogo}
          gruppoList={group}
          isEdit={dialogCatalogo.isEdit}
        />
      )}
      <BluAlert
        show={!!success}
        handleClose={() => {
          setSuccess("");
        }}
        text={success}
        type="success"
      />
      <BluAlert
        show={!!error}
        handleClose={() => {
          setError("");
        }}
        text={error}
        type="error"
      />
      <BluAlert
        show={fileTooBig}
        handleClose={() => {
          setFileTooBig(false);
        }}
        text={"Il file che hai inserito supera i 20MB"}
        type="warning"
      />
      <BluAlert
        show={confirmDelete !== null}
        handleClose={() => {
          setConfirmDelete(null);
        }}
        handleConfirm={handleDeleteConfirm}
        text={
          "Sei sicuro di voler eliminare questo Catalogo da Catalogo Bludata?"
        }
        type="warning"
      />
      <Stack flexDirection="column" flex={1} sx={{ height: "100%" }}>
        <BludataDefaultMainHeader
          showReload={true}
          text={"Configurazione / Cataloghi"}
          onRefreshTap={reloadCatalogo}
        />
        <Card variant="outlined" sx={{ height: "100%", p: 1, m: 0.5, mt: 0 }}>
          <Stack direction="column" spacing={1} sx={{ height: "100%" }}>
            <BluLoader open={loading} />
            <Typography>
              In questa sezione è possibile configurare i cataloghi. I campi
              Descrizione, Gruppi e Tipo sono obbligatori.
            </Typography>
            {loginData?.admin && (
              <>
                <DataGridPremium
                  apiRef={gridApiRef}
                  columns={columnsHandler(() => {
                    setFileTooBig(true);
                  })}
                  rows={!!catalogoList ? catalogoList : []}
                  onRowClick={(params) =>
                    setSelectedCatalogo(params.row as Catalogo)
                  }
                  getRowClassName={(params) =>
                    selectedCatalogo && params.id === selectedCatalogo.id
                      ? "selected-row"
                      : ""
                  }
                  sx={{
                    "& .MuiDataGrid-cell": {
                      outline: "none!important",
                    },
                    "& .MuiInputBase-root": {
                      border: "none!important",
                    },
                    "& .selected-row": {
                      backgroundColor: "rgba(0, 0, 255, 0.04)",
                    },
                    mt: 1,
                  }}
                />
              </>
            )}
            <Stack direction={"row"} justifyContent={"end"} spacing={0.5}>
              <Button
                variant="contained"
                color="primary"
                onClick={onNuovoClick}
              >
                Nuovo
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={() =>
                  selectedCatalogo && onEditClick(selectedCatalogo)
                }
                disabled={!selectedCatalogo}
              >
                Modifica
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={() =>
                  selectedCatalogo &&
                  selectedCatalogo.id &&
                  onDeleteClick(selectedCatalogo.id)
                }
                disabled={!selectedCatalogo}
              >
                Elimina
              </Button>
            </Stack>
          </Stack>
        </Card>
      </Stack>
    </Box>
  );
});
